import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  of,
  skip,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { MainTableInstrumentsService } from '@prlw/core/monitor/main-table-instruments.service';
import { DestinationState } from '@prlw/core/destinations/destination.state';
import {
  TARIFFS_GATEWAY,
  TariffsGateway,
} from '@prlw/core/tariffs/tariffs.gateway';

@Component({
  selector: 'prlw-edit-trader-tariff',
  templateUrl: './edit-trader-tariff.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditTraderTariffContainer implements OnInit, OnDestroy {
  @Input() instrumentCode: string | null = null;

  @Output() eventClose = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(
    private mainTableInstrumentsService: MainTableInstrumentsService,
    private destinationState: DestinationState,
    @Inject(TARIFFS_GATEWAY) private tariffsGateway: TariffsGateway,
  ) {}

  ngOnInit(): void {
    this.nextValue$
      .pipe(
        skip(1),
        withLatestFrom(
          this.price$,
          this.currentInstrument$,
          this.destinationState.currentDestination$,
          this.changeSimilarProductsTariff$,
        ),
        distinctUntilChanged(
          (prev, next) => prev[0] === next[0] && next[0] !== next[1],
        ),
        mergeMap(
          ([price, , instrument, destination, changeSimilarProductsTariff]) => {
            if (
              destination?.id &&
              instrument.productId &&
              instrument.shipmentBasisId
            ) {
              return this.tariffsGateway.updateTraderTariff({
                productId: instrument.productId,
                shipmentBasisId: instrument.shipmentBasisId,
                deliveryMethod: instrument.deliveryMethod,
                destinationStationId: parseInt(destination.id, 10),
                price,
                changeSimilarProductsTariff,
              });
            }
            return EMPTY;
          },
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  handleDelete() {
    of(1)
      .pipe(
        withLatestFrom(
          this.currentInstrument$,
          this.destinationState.currentDestination$,
          this.changeSimilarProductsTariff$,
        ),
        mergeMap(([, instrument, destination, changeSimilarProductsTariff]) => {
          if (
            destination?.id &&
            instrument.productId &&
            instrument.shipmentBasisId
          ) {
            return this.tariffsGateway.deleteTraderTariff({
              productId: instrument.productId,
              shipmentBasisId: instrument.shipmentBasisId,
              deliveryMethod: instrument.deliveryMethod,
              destinationStationId: parseInt(destination.id, 10),
              changeSimilarProductsTariff,
            });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.eventClose.emit();
      });
  }

  currentInstrument$ = this.mainTableInstrumentsService.instruments$.pipe(
    map(
      (items) =>
        items.filter((item) => item.instrumentCode === this.instrumentCode)[0],
    ),
    filter((item) => !!item),
  );

  shipmentBasisName$ = this.currentInstrument$.pipe(
    map((instrument) => instrument.shipment.name),
  );

  source$ = this.currentInstrument$.pipe(
    map((instrument) => instrument.tariffSource),
  );

  price$ = this.currentInstrument$.pipe(map((instrument) => instrument.tariff));

  deliveryMethod$ = this.currentInstrument$.pipe(
    map((instrument) => instrument.deliveryMethod),
  );

  nextValue$ = new Subject<number>();

  setPrice(event: FormControl) {
    if (event.valid) {
      this.nextValue$.next(event.value);
    }
  }

  changeSimilarProductsTariff$ = new BehaviorSubject(true);
  setChangeSimilarProductsTariff(value: boolean) {
    this.changeSimilarProductsTariff$.next(value);
  }
}
